import { useEffect, useRef } from "react";

/**
 * Divisore trascinabile tra due colonne di una pagina a pannelli
 * (Messaggi, Mail). Si accoppia a usePanelWidths (shared/ui/panelWidths).
 *
 * - drag col mouse/touch (pointer events, listener su window durante il drag
 *   cosi' il trascinamento non si perde uscendo dai 4px dell'handle);
 * - frecce tastiera ±16px (accessibilita': separator focusabile);
 * - doppio click = reset al default.
 *
 * `direction` e' il verso in cui il pannello controllato cresce quando il
 * puntatore si sposta a destra: 1 per i pannelli a sinistra del divisore
 * (sidebar, lista thread), -1 per il pannello a destra (ispettore contesto).
 */
export function PanelResizer({
  label,
  value,
  direction,
  onChange,
  onReset,
}: {
  label: string;
  value: number;
  direction: 1 | -1;
  onChange: (px: number) => void;
  onReset: () => void;
}) {
  // Sessione di drag corrente (null = nessun drag). Ref, non state: i
  // pointermove non devono ri-renderizzare l'handle.
  const dragRef = useRef<{ startX: number; startValue: number } | null>(null);
  const latestRef = useRef({ value, direction, onChange });
  latestRef.current = { value, direction, onChange };

  useEffect(() => {
    function handleMove(e: PointerEvent) {
      const drag = dragRef.current;
      if (!drag) return;
      const { direction: dir, onChange: change } = latestRef.current;
      change(drag.startValue + dir * (e.clientX - drag.startX));
    }
    function handleUp() {
      dragRef.current = null;
    }
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, []);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      tabIndex={0}
      title={`${label} (trascina, frecce ←→, doppio click = reset)`}
      onPointerDown={(e) => {
        // Solo tasto principale; evita drag fantasma col destro.
        if (e.button !== 0) return;
        e.preventDefault();
        dragRef.current = { startX: e.clientX, startValue: latestRef.current.value };
      }}
      onKeyDown={(e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const step = 16 * direction * (e.key === "ArrowRight" ? 1 : -1);
        onChange(value + step);
      }}
      onDoubleClick={onReset}
      className="h-full cursor-col-resize bg-line/60 transition-colors duration-[120ms] hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
    />
  );
}
